import { useState, useEffect, useRef } from "preact/hooks";
import { useConfig } from "./hooks/useConfig.js";
import { useChat } from "./hooks/useChat.js";
import { usePreviewConfig } from "./hooks/usePreviewConfig.js";
import { usePreviewChat } from "./hooks/usePreviewChat.js";
import { ChatHeader } from "./components/ChatHeader.js";
import { ChatBubble } from "./components/ChatBubble.js";
import { ChatWindow } from "./components/ChatWindow.js";
import { PreChatForm } from "./components/PreChatForm.js";
import { CsatRating } from "./components/CsatRating.js";
import { TeaserBubble } from "./components/TeaserBubble.js";
import { SuggestedQuestions } from "./components/SuggestedQuestions.js";
import { ConversationStarters } from "./components/ConversationStarters.js";
import { ChannelPicker } from "./components/ChannelPicker.js";
import { SalesCTA } from "./components/SalesCTA.js";
import { FarewellView } from "./components/FarewellView.js";

const FAREWELL_MS = 2000;

/** Resolve the sales-CTA destination URL from config, honoring target + auto-fallback. */
function resolveSalesHref(
  target: "auto" | "booking" | "whatsapp" | undefined,
  bookingUrl: string | null,
  whatsappUrl: string | null
): string | null {
  if (target === "whatsapp") return whatsappUrl;
  if (target === "booking") return bookingUrl;
  return bookingUrl || whatsappUrl; // "auto" (or unset)
}

function PreviewApp() {
  const { config, accessToken, tenantId, loading } = usePreviewConfig();
  const { messages, streaming, sendMessage, clearMessages, suggestions } = usePreviewChat(tenantId, accessToken);
  const [formCompleted, setFormCompleted] = useState(false);
  const [teaserPreview, setTeaserPreview] = useState<string | null>(null);
  const [open, setOpen] = useState(true);
  const [closing, setClosing] = useState(false);
  const [farewell, setFarewell] = useState(false);
  const farewellTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [channelChosen, setChannelChosen] = useState(false);
  const [salesCtaDismissed, setSalesCtaDismissed] = useState(false);

  // Listen for teaser preview messages from admin parent
  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      if (event.source !== window.parent) return;
      if (event.data?.type === "canary:preview-teaser") {
        setTeaserPreview(event.data.message || null);
      }
      if (event.data?.type === "canary:preview-teaser-dismiss") {
        setTeaserPreview(null);
      }
    }
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  // Reset channel choice when whatsapp config changes in preview
  useEffect(() => {
    setChannelChosen(false);
  }, [config?.whatsapp?.enabled, config?.whatsapp?.phoneNumber]);

  // Re-show the pre-chat form when the admin toggles it in the Playground —
  // otherwise a form skipped/submitted once in the preview stays hidden until
  // the iframe is reset, which reads as "the setting doesn't work".
  useEffect(() => {
    if (config?.preChatForm?.enabled) setFormCompleted(false);
  }, [config?.preChatForm?.enabled]);

  const doClose = () => {
    if (farewellTimerRef.current) {
      clearTimeout(farewellTimerRef.current);
      farewellTimerRef.current = null;
    }
    // Keep the farewell view mounted through the slide-down — clearing it
    // before the 150ms animation re-renders the chat underneath (flicker).
    setClosing(true);
    setTimeout(() => {
      setOpen(false);
      setClosing(false);
      setFarewell(false);
    }, 150);
  };

  const toggleOpen = () => {
    if (open) {
      // Closing an active conversation: show a brief thank-you first.
      // No conversation yet (or already in farewell) -> just close.
      if (!farewell && messages.length > 0) {
        setFarewell(true);
        farewellTimerRef.current = setTimeout(doClose, FAREWELL_MS);
        return;
      }
      doClose();
    } else {
      setOpen(true);
      setTeaserPreview(null);
    }
  };

  if (loading || !config) {
    return (
      <div class="fixed inset-0 flex items-center justify-center">
        <div class="flex flex-col items-center gap-3">
          <div class="size-8 rounded-full border-2 border-[var(--primary,#e5e7eb)] border-t-transparent animate-spin" />
          <span class="text-xs text-[var(--muted-foreground,#9ca3af)]">Loading...</span>
        </div>
      </div>
    );
  }

  const language = config.language || "en";
  const hasWhatsApp = !!(config.whatsapp?.enabled && config.whatsapp.phoneNumber);
  const needsForm =
    config.preChatForm?.enabled && !formCompleted && messages.length === 0;
  const needsChannelPicker = hasWhatsApp && !channelChosen && messages.length === 0 && !needsForm;
  const whatsappUrl = hasWhatsApp
    ? `https://wa.me/${config.whatsapp!.phoneNumber}${config.whatsapp!.prefillMessage ? `?text=${encodeURIComponent(config.whatsapp!.prefillMessage)}` : ""}`
    : null;

  const welcomeMessages =
    messages.length === 0 && !needsForm && !needsChannelPicker
      ? [{ id: "welcome", role: "assistant" as const, content: config.welcomeMessage }]
      : [];

  const showStarters = messages.length === 0 && !needsForm && !needsChannelPicker && (config.suggestedQuestions?.length ?? 0) > 0;

  const userRounds = messages.filter((m) => m.role === "user").length;
  const salesHref = config.salesCta?.enabled
    ? resolveSalesHref(config.salesCta.target, config.bookingUrl, whatsappUrl)
    : null;
  const showSalesCta =
    !!config.salesCta?.enabled &&
    !salesCtaDismissed &&
    !streaming &&
    !!salesHref &&
    userRounds >= config.salesCta.afterRounds;

  // When the sales CTA is showing it takes over as the sole suggestion —
  // hide AI follow-up chips so "Connect with Sales" stands alone.
  const showFollowUps =
    !streaming && messages.length > 0 && suggestions.length > 0 && !showSalesCta;

  function handleFormSubmit(_data: Record<string, string>) {
    setFormCompleted(true);
  }

  return (
    <div class="fixed inset-0 flex flex-col items-end justify-end p-4">
      {open ? (
        <div class={`w-full h-full max-w-[400px] max-h-[600px] ${closing ? "animate-slideDown" : "animate-slideUp"}`}>
          {farewell ? (
            <FarewellView botAvatar={config.botAvatar} language={language} onDismiss={doClose} />
          ) : needsForm ? (
            <div class="flex flex-col h-full bg-[var(--background)] rounded-2xl shadow-lg overflow-hidden">
              <ChatHeader botName={config.botName} onClose={toggleOpen} />
              <PreChatForm
                config={config.preChatForm!}
                botName={config.botName}
                language={language}
                blockedEmailDomains={config.blockedEmailDomains}
                onSubmit={handleFormSubmit}
              />
            </div>
          ) : needsChannelPicker ? (
            <div class="flex flex-col h-full bg-[var(--background)] rounded-2xl shadow-lg overflow-hidden">
              <ChatHeader botName={config.botName} onClose={toggleOpen} />
              <ChannelPicker
                botName={config.botName}
                botAvatar={config.botAvatar}
                phoneNumber={config.whatsapp!.phoneNumber}
                prefillMessage={config.whatsapp!.prefillMessage}
                onChatWithAI={() => setChannelChosen(true)}
              />
            </div>
          ) : (
            <ChatWindow
              messages={[...welcomeMessages, ...messages]}
              streaming={streaming}
              botName={config.botName}
              botAvatar={config.botAvatar}
              language={language}
              disclaimer={config.disclaimer}
              privacyPolicyUrl={config.privacyPolicyUrl}
              hideBranding={config.hideBranding}
              onSend={sendMessage}
              onClose={toggleOpen}
              whatsappUrl={whatsappUrl}
              startersSlot={
                showStarters
                  ? <ConversationStarters questions={config.suggestedQuestions!} onSelect={sendMessage} />
                  : undefined
              }
              followUpSlot={
                showFollowUps
                  ? <SuggestedQuestions questions={suggestions} onSelect={sendMessage} />
                  : undefined
              }
              salesCtaSlot={
                showSalesCta
                  ? <SalesCTA label={config.salesCta!.label} href={salesHref!} onDismiss={() => setSalesCtaDismissed(true)} />
                  : undefined
              }
              csatSlot={
                !streaming && messages.length > 0
                  ? <CsatRating tenantId={tenantId} sessionId="" conversationId="" language={language} preview />
                  : undefined
              }
            />
          )}
        </div>
      ) : (
        <div class="animate-fadeIn flex flex-col items-end">
          {teaserPreview && (
            <TeaserBubble
              message={teaserPreview}
              onOpen={() => { setTeaserPreview(null); setOpen(true); }}
              onDismiss={() => setTeaserPreview(null)}
            />
          )}
          <ChatBubble onClick={toggleOpen} />
        </div>
      )}
    </div>
  );
}

function LiveApp() {
  const params = new URLSearchParams(window.location.search);
  const tenantSlug = params.get("tenant") || "";
  const pageUrl = params.get("page") || "/";
  const utmRaw = params.get("utm");
  const utmParams = utmRaw ? (() => { try { return JSON.parse(utmRaw) as Record<string, string>; } catch { return null; } })() : null;
  const referrer = params.get("referrer") || undefined;
  const anonymousId = params.get("aid") || undefined;
  const { config, loading, error } = useConfig(tenantSlug);
  const [open, setOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const [farewell, setFarewell] = useState(false);
  const farewellTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [formCompleted, setFormCompleted] = useState(() => {
    if (typeof sessionStorage !== "undefined") {
      try { return sessionStorage.getItem("canary_visitor") !== null; } catch { return false; }
    }
    return false;
  });
  const [teaserMessage, setTeaserMessage] = useState<string | null>(null);
  const [proactiveWelcome, setProactiveWelcome] = useState<string | null>(null);
  const [salesCtaDismissed, setSalesCtaDismissed] = useState(false);
  const [channelChosen, setChannelChosen] = useState(() => {
    if (typeof sessionStorage !== "undefined") {
      try { return sessionStorage.getItem("canary_channel_chosen") !== null; } catch { return false; }
    }
    return false;
  });
  const teaserTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const language = config?.language || "en";
  const { messages, streaming, sendMessage, retryLast, setVisitor, resetChat, conversationId, sessionId, suggestions } = useChat(
    config?.tenantId || "",
    language,
    { pageUrl, utmParams, referrer, anonymousId }
  );

  // Notify parent iframe about open/close for resize
  const doClose = () => {
    if (farewellTimerRef.current) {
      clearTimeout(farewellTimerRef.current);
      farewellTimerRef.current = null;
    }
    // Keep the farewell view mounted through the slide-down — clearing it
    // before the 150ms animation re-renders the chat underneath (flicker).
    // Animate close: play slideDown, then unmount after animation
    setClosing(true);
    window.parent.postMessage({ type: "canary:resize", open: false }, "*");
    setTimeout(() => {
      setOpen(false);
      setClosing(false);
      setFarewell(false);
    }, 150);
  };

  const toggleOpen = () => {
    if (open) {
      // Closing an active conversation: show a brief thank-you first.
      // No conversation yet (or already in farewell) -> just close.
      if (!farewell && messages.length > 0) {
        setFarewell(true);
        farewellTimerRef.current = setTimeout(doClose, FAREWELL_MS);
        return;
      }
      doClose();
    } else {
      setOpen(true);
      // Clear teaser if user opens chat manually
      setTeaserMessage(null);
      if (teaserTimerRef.current) {
        clearTimeout(teaserTimerRef.current);
        teaserTimerRef.current = null;
      }
      window.parent.postMessage({ type: "canary:resize", open: true }, "*");
    }
  };

  // Announce position to the parent loader once config is available.
  // Declared before the proactive-message effect so it runs first per React ordering —
  // this guarantees the loader has placed the iframe before any teaser resize fires.
  useEffect(() => {
    if (!config) return;
    window.parent.postMessage(
      {
        type: "canary:ready",
        position: config.position,
        bottomOffset: config.bottomOffset ?? 0,
        sideOffset: config.sideOffset ?? 0,
      },
      "*"
    );
  }, [config]);

  // Proactive message trigger logic
  useEffect(() => {
    if (!config?.proactiveMessages?.length || open) return;

    const storageKey = `canary_proactive_${config.tenantId}`;
    if (typeof sessionStorage !== "undefined" && sessionStorage.getItem(storageKey)) return;

    // Find best matching rule: exact > prefix > wildcard
    const matches = config.proactiveMessages.filter((r) => {
      if (r.urlPattern === "*") return true;
      if (r.urlPattern.endsWith("*")) return pageUrl.startsWith(r.urlPattern.slice(0, -1));
      return pageUrl === r.urlPattern;
    });
    matches.sort((a, b) => {
      const aExact = !a.urlPattern.includes("*");
      const bExact = !b.urlPattern.includes("*");
      if (aExact !== bExact) return aExact ? -1 : 1;
      return b.urlPattern.length - a.urlPattern.length;
    });
    const rule = matches[0];
    if (!rule) return;

    teaserTimerRef.current = setTimeout(() => {
      if (!open) {
        setTeaserMessage(rule.message);
        if (typeof sessionStorage !== "undefined") {
          sessionStorage.setItem(storageKey, "1");
        }
        window.parent.postMessage(
          { type: "canary:resize", mode: "teaser" },
          "*"
        );
      }
    }, rule.delaySeconds * 1000);

    return () => {
      if (teaserTimerRef.current) clearTimeout(teaserTimerRef.current);
    };
  }, [config, open, pageUrl]);

  function handleTeaserOpen() {
    setProactiveWelcome(teaserMessage);
    setTeaserMessage(null);
    setOpen(true);
    window.parent.postMessage(
      { type: "canary:resize", open: true },
      "*"
    );
  }

  function handleTeaserDismiss() {
    setTeaserMessage(null);
    window.parent.postMessage(
      { type: "canary:resize", open: false },
      "*"
    );
  }

  function handleChannelChosen() {
    setChannelChosen(true);
    if (typeof sessionStorage !== "undefined") {
      try { sessionStorage.setItem("canary_channel_chosen", "1"); } catch {}
    }
  }

  if (loading || !config) {
    return null;
  }

  if (error) {
    return (
      <div class="fixed bottom-4 right-4 max-w-[280px] rounded-xl border border-[var(--border)] bg-[var(--background)] p-4 shadow-lg text-sm text-[var(--muted-foreground)]">
        <p>Chat is temporarily unavailable. Please try again later.</p>
      </div>
    );
  }

  const hasWhatsApp = !!(config.whatsapp?.enabled && config.whatsapp.phoneNumber);
  const needsForm =
    config.preChatForm?.enabled && !formCompleted && messages.length === 0;
  const needsChannelPicker = hasWhatsApp && !channelChosen && messages.length === 0 && !needsForm;
  const whatsappUrl = hasWhatsApp
    ? `https://wa.me/${config.whatsapp!.phoneNumber}${config.whatsapp!.prefillMessage ? `?text=${encodeURIComponent(config.whatsapp!.prefillMessage)}` : ""}`
    : null;

  // Prepend welcome message if no conversation yet and form is done (or not needed)
  const displayWelcome = proactiveWelcome || config.welcomeMessage;
  const welcomeMessages =
    messages.length === 0 && !needsForm && !needsChannelPicker
      ? [
          {
            id: "welcome",
            role: "assistant" as const,
            content: displayWelcome,
          },
        ]
      : [];

  const showStarters = messages.length === 0 && !needsForm && !needsChannelPicker && (config.suggestedQuestions?.length ?? 0) > 0;

  // Sales CTA: surface after N conversation rounds (= user messages), once a
  // destination resolves. Dismiss hides it for the rest of the conversation.
  const userRounds = messages.filter((m) => m.role === "user").length;
  const salesHref = config.salesCta?.enabled
    ? resolveSalesHref(config.salesCta.target, config.bookingUrl, whatsappUrl)
    : null;
  const showSalesCta =
    !!config.salesCta?.enabled &&
    !salesCtaDismissed &&
    !streaming &&
    !!salesHref &&
    userRounds >= config.salesCta.afterRounds;

  // When the sales CTA is showing it takes over as the sole suggestion —
  // hide AI follow-up chips so "Connect with Sales" stands alone.
  const showFollowUps =
    !streaming && messages.length > 0 && suggestions.length > 0 && !showSalesCta;

  function handleFormSubmit(data: Record<string, string>) {
    setVisitor({ name: data.name || "", email: data.email || "", ...data });
    setFormCompleted(true);
  }

  return (
    <div class="fixed inset-0 flex flex-col items-end justify-end p-4">
      {open ? (
        <div class={`w-full h-full max-w-[400px] max-h-[600px] ${closing ? "animate-slideDown" : "animate-slideUp"}`}>
          {farewell ? (
            <FarewellView botAvatar={config.botAvatar} language={language} onDismiss={doClose} />
          ) : needsForm ? (
            <div class="flex flex-col h-full bg-[var(--background)] rounded-2xl shadow-lg overflow-hidden">
              <ChatHeader botName={config.botName} onClose={toggleOpen} />
              <PreChatForm
                config={config.preChatForm!}
                botName={config.botName}
                language={language}
                blockedEmailDomains={config.blockedEmailDomains}
                onSubmit={handleFormSubmit}
              />
            </div>
          ) : needsChannelPicker ? (
            <div class="flex flex-col h-full bg-[var(--background)] rounded-2xl shadow-lg overflow-hidden">
              <ChatHeader botName={config.botName} onClose={toggleOpen} />
              <ChannelPicker
                botName={config.botName}
                botAvatar={config.botAvatar}
                phoneNumber={config.whatsapp!.phoneNumber}
                prefillMessage={config.whatsapp!.prefillMessage}
                onChatWithAI={handleChannelChosen}
              />
            </div>
          ) : (
            <ChatWindow
              messages={[...welcomeMessages, ...messages]}
              streaming={streaming}
              botName={config.botName}
              botAvatar={config.botAvatar}
              language={language}
              disclaimer={config.disclaimer}
              privacyPolicyUrl={config.privacyPolicyUrl}
              hideBranding={config.hideBranding}
              onSend={sendMessage}
              onClose={toggleOpen}
              whatsappUrl={whatsappUrl}
              startersSlot={
                showStarters
                  ? <ConversationStarters questions={config.suggestedQuestions!} onSelect={sendMessage} />
                  : undefined
              }
              followUpSlot={
                showFollowUps
                  ? <SuggestedQuestions questions={suggestions} onSelect={sendMessage} />
                  : undefined
              }
              salesCtaSlot={
                showSalesCta
                  ? <SalesCTA label={config.salesCta!.label} href={salesHref!} onDismiss={() => setSalesCtaDismissed(true)} />
                  : undefined
              }
              csatSlot={
                !streaming && messages.length > 0 && conversationId
                  ? <CsatRating tenantId={config.tenantId} sessionId={sessionId} conversationId={conversationId} language={language} />
                  : undefined
              }
              onNewChat={messages.length > 0 ? resetChat : undefined}
            />
          )}
        </div>
      ) : (
        <div class="animate-fadeIn flex flex-col items-end">
          {teaserMessage && (
            <TeaserBubble
              message={teaserMessage}
              onOpen={handleTeaserOpen}
              onDismiss={handleTeaserDismiss}
            />
          )}
          <ChatBubble onClick={toggleOpen} />
        </div>
      )}
    </div>
  );
}

export function App() {
  const params = new URLSearchParams(window.location.search);
  const isPreview = params.get("mode") === "preview";

  // Each branch is its own component with its own hooks — no rules-of-hooks violation
  return isPreview ? <PreviewApp /> : <LiveApp />;
}
